import type { TFunction } from "i18next";
import type { CityHallStateResponse } from "../../client";
import UserAvatar from "../../components/UserAvatar";
import ElectionsActiveBanner from "../../ui/cityhall/ElectionsActiveBanner";
import { formatUserName } from "../../utils/format/formatUserName";
import { SHELL_ICON } from "./constants";


interface MayorInfoScreenProps {
  formatDate: (dateString: string | null | undefined) => string;
  formatElectionTimeRemaining: (electionEndAt: string | null | undefined) => string;
  formatTimeRemaining: (termEndAt: string | null | undefined) => string;
  setCurrentScreen: (screen: "main") => void;
  state: CityHallStateResponse | undefined;
  t: TFunction;
}

export function MayorInfoScreen({
  formatDate,
  formatElectionTimeRemaining,
  formatTimeRemaining,
  setCurrentScreen,
  state,
  t,
}: MayorInfoScreenProps) {
  return (
        <div
          className="cityhall-scrollable absolute top-0 left-1/2 bottom-0 z-[10004] flex w-full max-w-[332px] -translate-x-1/2 flex-col gap-3xl overflow-x-hidden overflow-y-auto pt-[54px] pb-2xl"
        >
          <div className="flex flex-col items-start self-stretch rounded-xl border border-brand bg-white p-3xl">
            <div
              className="mb-xl [font-family:'Rubik_One',sans-serif] text-[20px] leading-[40px] font-normal text-deep-blue"
            >
              {t("Elder")}
            </div>

            {state?.has_mayor && state?.mayor ? (
              <>
                <div
                  className="mb-3xl w-full text-center [font-family:Lato,sans-serif] text-[18px] leading-[30px] font-normal text-text-dark"
                >
                  {t("Information about the current mayor:")}
                </div>
                
                {/* Аватарка по центру */}
                <div className="mb-3xl flex w-full flex-col items-center">
                  <UserAvatar
                    user={state.mayor}
                    size={120}
                    backgroundColor="var(--color-deep-blue-alpha-20)"
                    textColor={'var(--color-deep-blue)'}
                    alt={formatUserName(state.mayor).shortName}
                  />

                  {/* Повне ім'я */}
                  <div
                    className="mt-xl text-center [font-family:Lato,sans-serif] text-[20px] leading-[29px] font-bold text-deep-blue"
                  >
                    {formatUserName(state.mayor).shortName}
                  </div>

                  {/* Логін (якщо є) */}
                  {state.mayor.username && (
                    <div
                      className="mt-xs text-center [font-family:Lato,sans-serif] text-[16px] leading-[24px] font-normal text-text-muted"
                    >
                      @{state.mayor.username}
                    </div>
                  )}

                  {/* Ім'я перше і друге (якщо є) */}
                  {(state.mayor.first_name || state.mayor.last_name) && (
                    <div
                      className="mt-xs text-center [font-family:Lato,sans-serif] text-[16px] leading-[24px] font-normal text-text-muted"
                    >
                      {[
                        state.mayor.first_name,
                        state.mayor.last_name
                      ].filter(Boolean).join(' ')}
                    </div>
                  )}

                  {/* Рівень мера */}
                  {state.mayor.level && (
                    <div
                      className="mt-lg text-center [font-family:Lato,sans-serif] text-[18px] leading-[26px] font-semibold text-brand"
                    >
                      lvl: {state.mayor.level}
                    </div>
                  )}
                </div>

                {/* Інформація про термін */}
                <div
                  className="[font-family:Lato,sans-serif] text-[18px] leading-[30px] font-normal text-text-dark"
                >
                  <div className="mb-lg">
                    <div className="mb-xs font-semibold">
                      {t("Term start date")}:
                    </div>
                    <div
                      className="[direction:ltr] [font-family:Lato,sans-serif] [unicode-bidi:isolate] text-[18px] leading-[30px] font-normal text-text-dark"
                    >
                      {formatDate(state.mayor.term_start_at)}
                    </div>
                  </div>
                  <div className="mb-lg">
                    <div className="mb-xs font-semibold">
                      {t("Term end date")}:
                    </div>
                    <div
                      className="[direction:ltr] [font-family:Lato,sans-serif] [unicode-bidi:isolate] text-[18px] leading-[30px] font-normal text-text-dark"
                    >
                      {formatDate(state.mayor.term_end_at)}
                    </div>
                  </div>
                  <div>
                    <div className="mb-xs font-semibold">
                      {t("Time remaining")}:
                    </div>
                    <div
                      className="[direction:ltr] [font-family:Lato,sans-serif] [unicode-bidi:isolate] text-[18px] leading-[30px] font-normal text-brand"
                    >
                      {formatTimeRemaining(state.mayor.term_end_at)}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div
                className="[font-family:Lato,sans-serif] text-[18px] leading-[30px] font-normal text-text-dark"
              >
                <div className="mb-lg">
                  {t("There is currently no Mayor!")}
                </div>
                <div className="mb-lg">
                  {t("You can become the Mayor!")}
                </div>
                <div>
                  {t("Hurry and register your candidacy!!!")}
                </div>
              </div>
            )}

            {/* Elections info if active */}
            {state?.voting_available && (
              <ElectionsActiveBanner
                electionEndAt={state.election_end_at}
                formatElectionTimeRemaining={formatElectionTimeRemaining}
                marginTop="24px"
              />
            )}
          </div>

          {/* Back button */}
          <div className="mt-lg flex justify-end">
            <button
              onClick={() => setCurrentScreen("main")}
              className="flex w-fit cursor-pointer items-center justify-center gap-sm rounded-pill border-none bg-[image:var(--gradient-action-primary)] px-[48px] py-lg [font-family:'Rubik_One',sans-serif] text-[16px] leading-[24px] font-normal text-white shadow-action-raised"
            >
              {t("Back")}
            </button>
          </div>
        </div>
  );
}

interface MayorRoleScreenProps {
  setCurrentScreen: (screen: "main") => void;
  t: TFunction;
}

export function MayorRoleScreen({
  setCurrentScreen,
  t,
}: MayorRoleScreenProps) {
  return (
        <div
          className="cityhall-scrollable absolute top-0 left-1/2 bottom-0 z-[10004] flex w-full max-w-[332px] -translate-x-1/2 flex-col gap-3xl overflow-x-hidden overflow-y-auto pt-[54px] pb-2xl"
        >
          <div className="flex flex-col items-start self-stretch whitespace-pre-wrap rounded-xl border border-brand bg-white p-3xl">
            <div
              className="mb-0 [font-family:'Rubik_One',sans-serif] text-[20px] leading-[40px] font-normal text-deep-blue"
            >
              {t("Elder")}
            </div>
            <div
              className="whitespace-pre-line [font-family:Lato,sans-serif] text-[18px] leading-[30px] font-normal text-text-dark"
            >
              {t("The Mayor is a player respected and loved by all across the island — the Elder chosen by the citizens to guide the town. With the office come time-honored privileges, granted only to the one the people trust the most.")}
            </div>
            {/* Gold perk — pearl-mining privilege */}
            <div
              className="mt-xl flex items-center gap-lg self-stretch rounded-[14px] bg-[linear-gradient(135deg,var(--color-gold),var(--color-accent))] px-[18px] py-lg shadow-[0_2px_10px_var(--color-accent-alpha-35)]"
            >
              <span className="shrink-0 text-[24px] leading-none">👑</span>
              <div className="flex flex-col gap-[1px]">
                <span
                  className="[font-family:Lato,sans-serif] text-[11px] leading-[16px] font-bold tracking-[1.2px] text-info-screens-text uppercase"
                >
                  {t("Mayoral Privilege")}
                </span>
                <span
                  className="[font-family:Lato,sans-serif] text-[17px] leading-[22px] font-bold text-info-screens-text-2"
                >
                  {t("+20% pearl mining")}
                </span>
                <span
                  className="[font-family:Lato,sans-serif] text-[13px] leading-[18px] font-normal text-info-screens-text-3"
                >
                  {t("Combines with VIP")}
                </span>
              </div>
            </div>

            {/* Amethyst perk — exclusive City Hall office */}
            <div
              className="mt-lg flex items-center gap-lg self-stretch rounded-[14px] bg-[linear-gradient(135deg,var(--color-info-screens-surface),var(--color-info-screens-surface-2))] px-[18px] py-lg shadow-[0_2px_10px_var(--color-duel-ghost-alpha-40)]"
            >
              <span className="shrink-0 text-[24px] leading-none">🏛️</span>
              <div className="flex flex-col gap-[1px]">
                <span
                  className="[font-family:Lato,sans-serif] text-[11px] leading-[16px] font-bold tracking-[1.2px] text-info-screens-text-4 uppercase"
                >
                  {t("Mayor's Chamber")}
                </span>
                <span
                  className="[font-family:Lato,sans-serif] text-[17px] leading-[22px] font-bold text-white"
                >
                  {t("Exclusive Mayor's office")}
                </span>
                <span
                  className="[font-family:Lato,sans-serif] text-[13px] leading-[18px] font-normal text-info-screens-text-5"
                >
                  {t("Furnish your office in the City Hall to your own taste")}
                </span>
              </div>
            </div>
          </div>

          {/* Back button */}
          <div className="mt-lg flex justify-end">
            <button
              onClick={() => setCurrentScreen("main")}
              className="flex w-fit cursor-pointer items-center justify-center gap-sm rounded-pill border-none bg-[image:var(--gradient-action-primary)] px-[48px] py-lg [font-family:'Rubik_One',sans-serif] text-[16px] leading-[24px] font-normal text-white shadow-action-raised"
            >
              {t("Back")}
            </button>
          </div>
        </div>
  );
}

interface ElectionsInfoScreenProps {
  setCurrentScreen: (screen: "main") => void;
  state: CityHallStateResponse;
  t: TFunction;
}

export function ElectionsInfoScreen({
  setCurrentScreen,
  state,
  t,
}: ElectionsInfoScreenProps) {
  return (
        <div
          className="cityhall-scrollable absolute top-0 left-1/2 bottom-0 z-[10004] flex w-full max-w-[332px] -translate-x-1/2 flex-col gap-3xl overflow-x-hidden overflow-y-auto pt-[54px] pb-2xl"
        >
          <div className="flex flex-col items-start self-stretch whitespace-pre-wrap rounded-xl border border-brand bg-white p-3xl">
            <div
              className="mb-0 [font-family:'Rubik_One',sans-serif] text-[20px] leading-[40px] font-normal text-deep-blue"
            >
              {t("Elder")}
            </div>
            <div
              className="[font-family:Lato,sans-serif] text-[18px] leading-[30px] font-normal text-text-dark"
            >
              <div className="mb-2xl">
                <div className="mb-lg font-semibold text-deep-blue">
                  {t("A new election can begin when:")}
                </div>
                <ul className="m-0 list-disc pl-2xl">
                  <li className="mb-md">
                    {t("There is no active Mayor, or the Mayor's term is finished.")}
                  </li>
                  <li className="mb-md">
                    {t("At least one player has registered as a candidate.")}
                  </li>
                </ul>
              </div>

              <div className="mb-2xl font-semibold text-deep-blue">
                {t("The Mayor is elected for a term of 1 week (7 days)!")}
              </div>

              <div className="mb-2xl">
                <div className="mb-md">
                  {t("Any player can become a candidate by paying:")}{" "}
                  <span className="inline-flex items-center gap-xs align-middle">
                    {state.registration_price_pearls.toLocaleString("en-US")}
                    <img src={SHELL_ICON} alt="" className="h-[16px] w-[20px]" />
                  </span>
                  .
                </div>
                <div className="text-[16px] text-text-muted italic">
                  {t("This payment is only for registration and does not give any votes!")}
                </div>
              </div>

              <div className="mb-2xl font-semibold text-deep-blue">
                {t("To win, you must collect more votes than other candidates!")}
              </div>

              <div className="mb-2xl">
                <div className="mb-md">
                  {t("When the election begins, all players can vote.")}
                </div>
                <div className="mb-md">
                  {t("1 vote costs:")}{" "}
                  <span className="inline-flex items-center gap-xs align-middle">
                    {state.vote_price_pearls.toLocaleString("en-US")}
                    <img src={SHELL_ICON} alt="" className="h-[16px] w-[20px]" />
                  </span>
                  .
                </div>
                <div className="mb-md">
                  {t("The voting phase lasts 4 days.")}
                </div>
              </div>

              <div className="font-semibold text-deep-blue">
                {t("The player with the most votes becomes the new Mayor.")}
              </div>
            </div>
          </div>

          {/* Back button */}
          <div className="mt-lg flex justify-end">
            <button
              onClick={() => setCurrentScreen("main")}
              className="flex w-fit cursor-pointer items-center justify-center gap-sm rounded-pill border-none bg-[image:var(--gradient-action-primary)] px-[48px] py-lg [font-family:'Rubik_One',sans-serif] text-[16px] leading-[24px] font-normal text-white shadow-action-raised"
            >
              {t("Back")}
            </button>
          </div>
        </div>
  );
}
